import { FC } from 'react';
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { colors, fontWeights } from '../../theme/tokens';
import AssemblingProgress from './AssemblingProgress';
import CopyButton from './CopyButton';
import PageViewer from './PageViewer';
import { DocumentPage, MetadataValues, QrSummary } from './types';

// Step 3 — "Valider les infos" (XD screen 5): the read-only
// summary with the same label order as step 2, "URL de
// vérification" with the copy icon, and the preview panel. The
// "Pages · N" row is added with the thumbnail strip (ROADMAP.md, Part 3).
//
// The values read in light orange and the URL carries an open-link icon
// beside the copy one: both the smartphone XD (S6) and the "Flux 2"
// desktop revision draw them that way (DESIGN.md).
//
// The verification URL is the one the assembly inherits from the pages when
// their codes match (`consistent`); with no code, "—".
//
// During assembly, the progress bar replaces the panel: with ten
// pages it is seconds and the user must see that something is happening.

type Props = {
  values: MetadataValues;
  serviceName: string;
  issuerName: string;
  pages: DocumentPage[];
  qr: QrSummary;
  pageIndex: number;
  onPageIndexChange: (index: number) => void;
  assembling: boolean;
};

const Row: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box
    sx={(theme) => ({
      display: 'grid',
      gridTemplateColumns: '150px minmax(0, 1fr)',
      columnGap: 2,
      alignItems: 'center',
      [theme.breakpoints.down('tablet')]: {
        gridTemplateColumns: 'minmax(0, 1fr)',
        rowGap: 0.25,
      },
    })}
  >
    <Typography
      component="dt"
      sx={(theme) => ({
        fontSize: '0.875rem',
        textAlign: 'right',
        opacity: 0.85,
        [theme.breakpoints.down('tablet')]: { textAlign: 'left' },
      })}
    >
      {label}
    </Typography>
    <Typography
      component="dd"
      sx={{
        m: 0,
        fontWeight: fontWeights.medium,
        minWidth: 0,
        overflowWrap: 'anywhere',
        color: colors.accentLight,
      }}
    >
      {children}
    </Typography>
  </Box>
);

const Step3Summary: FC<Props> = ({
  values,
  serviceName,
  issuerName,
  pages,
  qr,
  pageIndex,
  onPageIndexChange,
  assembling,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  // A document carrying several codes answers to each of them; its record
  // shows the first, as the backend keeps it.
  const url =
    qr.status === 'consistent' ? qr.url : qr.status === 'mismatch' ? qr.urls[0] : '';

  return (
    <Box
      sx={(t) => ({
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)',
        columnGap: 4,
        rowGap: 3,
        [t.breakpoints.down('tablet')]: { gridTemplateColumns: 'minmax(0, 1fr)' },
      })}
    >
      <Box component="dl" sx={{ m: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Row label="Titre du document">{values.title || '—'}</Row>
        <Row label="Date d'émission">
          {values.issuingDate ? values.issuingDate.format('DD/MM/YYYY') : '—'}
        </Row>
        <Row label="Service">{serviceName}</Row>
        <Row label="Émetteur">{issuerName}</Row>
        <Row label="Confidentiel">{values.confidential ? 'Oui' : 'Non'}</Row>
        <Row label="Pages">{pages.length}</Row>
        <Row label="URL de vérification">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography
              component="span"
              data-testid="verification-url"
              sx={{ fontWeight: fontWeights.medium, overflowWrap: 'anywhere' }}
            >
              {url || '—'}
            </Typography>
            {url && (
              <>
                <CopyButton
                  value={url}
                  label="Copier l'URL de vérification"
                  color={colors.accentLight}
                />
                <IconButton
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Ouvrir l'URL de vérification"
                  size="small"
                  sx={{ color: colors.accentLight }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
        </Row>
      </Box>

      <Box>
        {assembling ? (
          <AssemblingProgress pageCount={pages.length} />
        ) : (
          <PageViewer
            pages={pages}
            qr={qr}
            index={pageIndex}
            onIndexChange={onPageIndexChange}
            compact={isMobile}
          />
        )}
      </Box>
    </Box>
  );
};

export default Step3Summary;
